/**
 * Root React component for the PWA.
 * Handles auth guard, responsive layout, view routing, and theme restoration.
 */

import { useEffect } from 'react';
import { useAuthStore } from './store/auth';
import { useAppStore } from './store/app';
import { useThemeStore } from './store/theme';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { useSync } from './hooks/useSync';
import { LoginView } from './views/LoginView';
import { TodayView } from './views/TodayView';
import { CalendarView } from './views/CalendarView';
import { TasksView } from './views/TasksView';
import { NotesView } from './views/NotesView';
import { SettingsView } from './views/SettingsView';
import { DownloadView } from './views/DownloadView';
import { BottomTabBar } from './components/BottomTabBar';
import { SideNav } from './components/SideNav';
import { OfflineIndicator } from './components/OfflineIndicator';
import { ConflictToast } from './components/ConflictToast';

function AppContent() {
  const activeView = useAppStore((s) => s.activeView);

  switch (activeView) {
    case 'today':
      return <TodayView />;
    case 'calendar':
      return <CalendarView />;
    case 'tasks':
      return <TasksView />;
    case 'notes':
      return <NotesView />;
    case 'settings':
      return <SettingsView />;
    case 'download':
      return <DownloadView />;
    default:
      return <TodayView />;
  }
}

export function App() {
  const token = useAuthStore((s) => s.token);
  const restoreTheme = useThemeStore((s) => s.restore);

  // Track online status and install prompt
  useOnlineStatus();
  useInstallPrompt();

  // Real-time sync via WebSocket (connects after login)
  useSync();

  // Restore theme on mount
  useEffect(() => {
    restoreTheme();
  }, [restoreTheme]);

  // Auth guard: show login if no JWT in memory
  if (!token) {
    return <LoginView />;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)' }}>
      <OfflineIndicator />
      <ConflictToast />
      <div className="flex flex-1 overflow-hidden" style={{ paddingTop: 'var(--sat)' }}>
        <SideNav />
        <main className="flex-1 overflow-hidden" style={{ paddingBottom: 'var(--sab)' }}>
          <AppContent />
        </main>
      </div>
      <BottomTabBar />
    </div>
  );
}
